import { useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../../components/ui/alert-dialog';
import { Button } from '../../../../../components/ui/button';
import { useHasProAccess } from '../../../../../core/licensing';
import { cancelPendingSessionAutoSave } from '../../../../sessions/services/sessionAutoSave';
import { useSessionStore } from '../../../../sessions/store/sessionStore';
import { formatTimeAgo } from '../../../../sessions/utils/sessionDisplayUtils';

export function CurrentSessionSection() {
  const { currentSession, hasSavedSession, clearSession, deleteAllSessions } = useSessionStore();
  const isProMode = useHasProAccess();

  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const hasSession = hasSavedSession();

  const sessionSummary = useMemo(() => {
    if (!currentSession) return null;

    return {
      name: currentSession.name || 'Untitled Session',
      lastOpenedAt: currentSession.lastOpenedAt,
      tabsCount: currentSession.tabs.length,
      connectionsCount: currentSession.connections.length,
    };
  }, [currentSession]);

  const handleConfirmClear = () => {
    cancelPendingSessionAutoSave();

    if (isProMode) {
      clearSession();
    } else {
      deleteAllSessions();
    }

    setShowClearConfirm(false);
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">Current Session</div>

      {hasSession && sessionSummary ? (
        <div data-testid="current-session-info" className="space-y-3">
          <div className="rounded-md border border-border bg-background p-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium truncate">{sessionSummary.name}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {sessionSummary.connectionsCount} connection{sessionSummary.connectionsCount === 1 ? '' : 's'} •{' '}
                  {sessionSummary.tabsCount} tab{sessionSummary.tabsCount === 1 ? '' : 's'}
                </div>
              </div>
              <div className="shrink-0 text-xs text-muted-foreground">
                {formatTimeAgo(sessionSummary.lastOpenedAt)}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setShowClearConfirm(true)}
              data-testid="clear-session-button"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Clear Session
            </Button>
          </div>
        </div>
      ) : (
        <div
          data-testid="no-session-info"
          className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
        >
          No session saved yet.
        </div>
      )}

      <AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <AlertDialogContent data-testid="clear-session-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear session?</AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogDescription>
            This removes the persisted session snapshot from disk. You can keep working, but the app will not be able
            to resume this session later.
          </AlertDialogDescription>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-session-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClear}
              className="bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill/90"
              data-testid="clear-session-confirm"
            >
              Clear Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

